import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export interface ControlledSubstanceEntry {
  id: string
  medication_id: string
  patient_id: string | null
  prescription_id: string | null
  transaction_type: 'Received' | 'Dispensed' | 'Wasted' | 'Adjustment'
  quantity: number
  running_balance: number
  performed_by: string
  witnessed_by: string
  notes: string
  transaction_date: string
  created_at: string
  medication_name: string
  dea_schedule: string | null
  patient_name: string | null
  prescription_number: number | null
}

export interface ControlledMedication {
  id: string
  name: string
  dea_schedule: string | null
}

export interface PatientOption {
  id: string
  name: string
  species: string
  owner_name: string
}

export interface PrescriptionOption {
  id: string
  prescription_number: number
}

interface ControlledSubstancesState {
  entries: ControlledSubstanceEntry[]
  medications: ControlledMedication[]
  loading: boolean
  error: string | null
}

const initialState: ControlledSubstancesState = {
  entries: [],
  medications: [],
  loading: false,
  error: null,
}

export const fetchControlledSubstances = createAsyncThunk(
  'controlledSubstances/fetch',
  async () => {
    const res = await fetch('/.netlify/functions/controlled-substances')
    if (!res.ok) throw new Error('Failed to fetch controlled substances log')
    return res.json() as Promise<ControlledSubstanceEntry[]>
  }
)

export const fetchControlledMedications = createAsyncThunk(
  'controlledSubstances/fetchMedications',
  async () => {
    const res = await fetch('/.netlify/functions/controlled-substances-medications')
    if (!res.ok) throw new Error('Failed to fetch controlled medications')
    return res.json() as Promise<ControlledMedication[]>
  }
)

export const addControlledSubstanceEntry = createAsyncThunk(
  'controlledSubstances/add',
  async (data: {
    medication_id: string
    transaction_type: string
    quantity: number
    patient_id?: string
    prescription_id?: string
    performed_by: string
    witnessed_by?: string
    notes?: string
  }) => {
    const res = await fetch('/.netlify/functions/controlled-substances', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to add controlled substance entry')
    return res.json() as Promise<ControlledSubstanceEntry>
  }
)

const controlledSubstancesSlice = createSlice({
  name: 'controlledSubstances',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchControlledSubstances.pending, (state) => {
        if (state.entries.length === 0) state.loading = true
      })
      .addCase(fetchControlledSubstances.fulfilled, (state, action) => {
        state.entries = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(fetchControlledSubstances.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to load'
      })
      .addCase(fetchControlledMedications.fulfilled, (state, action) => {
        state.medications = action.payload
      })
      .addCase(addControlledSubstanceEntry.fulfilled, (state, action) => {
        state.entries.unshift(action.payload)
      })
  },
})

export default controlledSubstancesSlice.reducer
